import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ClipboardCopy,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Switch,
  Tab,
  TabTitleText,
  Tabs,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, MicrochipIcon, PlusCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { useGpuServers } from '../../../hooks/useGpuServers';
import { useWallet } from '../../../hooks/useWallet';
import type { GpuServer } from '../../../types/models/GpuServer';
import { formatDateTime } from '../../../utils/dateFormat';
import { formatPrice } from '../../../utils/money';
import { BalanceAmount } from '../../Organization/Billing/paymentDisplay';
import TopUpModal from '../../Organization/Billing/TopUpModal';
import GpuBudgetTab from './Budget/GpuBudgetTab';
import GpuServerImpersonationAlert from './GpuServerImpersonationAlert';
import GpuServerStatusLabel from './GpuServerStatusLabel';
import { GPU_SERVER_MAX_ACTIVE } from './gpuServerConstants';
import { activeServerCount, billedServers, earliestPaidThrough, isShortOf, nextHourTotal } from './gpuServerBilling';
import { isBilled, isTerminal } from './gpuServerStatus';

const START_PATH = '/datacenter/gpu-servers/new';

// The page's tabs, URL-backed as `?tab=`: `servers` is the default (no `?tab=`), and any unknown value
// falls back to it. Never a path segment: `/datacenter/gpu-servers/budget` would match `:serverId`.
const GPU_SERVERS_TAB_KEYS = ['servers', 'budget'] as const;
type GpuServersTabKey = (typeof GPU_SERVERS_TAB_KEYS)[number];

const isGpuServersTabKey = (value: unknown): value is GpuServersTabKey =>
  typeof value === 'string' && (GPU_SERVERS_TAB_KEYS as readonly string[]).includes(value);

/** "Paid until …" while billed, "Deleted …" once deleted, "—" otherwise (failed / unavailable / deleting). */
const billingCell = (server: GpuServer): string => {
  if (isBilled(server.status)) {
    return `Paid until ${formatDateTime(server.paid_through)}`;
  }
  if (server.status === 'deleted' && server.destroyed_at !== null) {
    return `Deleted ${formatDateTime(server.destroyed_at)}`;
  }
  return '—';
};

/**
 * "Start a server" — a link to the wizard, or an aria-disabled button with the reason in a tooltip
 * (impersonation, or the per-organisation cap). The disabled form is a plain button, never a link, so
 * it can't navigate.
 */
const StartServerButton: React.FunctionComponent<{ disabledReason: string | null }> = ({ disabledReason }) => {
  if (disabledReason === null) {
    return (
      <Button variant="primary" icon={<PlusCircleIcon />} component={(props) => <Link {...props} to={START_PATH} />}>
        Start a server
      </Button>
    );
  }
  return (
    <Tooltip content={disabledReason}>
      <Button variant="primary" icon={<PlusCircleIcon />} isAriaDisabled>
        Start a server
      </Button>
    </Tooltip>
  );
};

/**
 * Datacenter › GPU Servers — the organisation's prepaid GPU / CPU servers (docs/api-types/gpu-servers.md),
 * each paid hour by hour, in advance, from the prepaid balance.
 *
 * Two URL-backed tabs (`?tab=`, replace): Servers (the default, and every existing link's landing) and
 * Budget (`?tab=budget&budget=<id>` — saved monthly estimates, docs/api-types/gpu-budgets.md). The Tabs
 * mount lazily (`mountOnEnter`), so the budget reads only run once the Budget tab is opened; leaving it
 * drops `budget=`. The header's Start and Refresh belong to the Servers tab and show only there; the
 * servers and wallet reads stay at page level because the header needs them.
 *
 * A compact table with no search and no pagination — an explicit opt-out from the list-table rule
 * (Bruno, 2026-09-28): an organisation runs at most 5 active servers, and the history of terminal
 * servers stays behind the "Show deleted servers" switch.
 *
 * The list polls silently (every 30 s) only while a server is starting, being deleted or unpaid. The
 * offer — a live upstream read — is never loaded here: the 5-server cap is the `GPU_SERVER_MAX_ACTIVE`
 * constant, and the backend enforces the real one.
 */
function GpuServers() {
  const auth = useAuth();
  const { isImpersonating } = useImpersonation();
  const { servers, loading, error, pollError, refetch } = useGpuServers();
  const {
    wallet,
    defaultPaymentMethod,
    loading: walletLoading,
    error: walletError,
    refetch: refetchWallet,
  } = useWallet();
  const [showDeleted, setShowDeleted] = useState(false);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: GpuServersTabKey = isGpuServersTabKey(requestedTab) ? requestedTab : 'servers';

  const selectTab = (key: GpuServersTabKey) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'servers') {
      next.delete('tab');
    } else {
      next.set('tab', key);
    }
    // The selected budget belongs to the Budget tab; leaving it drops the parameter.
    if (key !== 'budget') {
      next.delete('budget');
    }
    setSearchParams(next, { replace: true });
  };

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const firstLoad = loading && servers === null;
  const allServers = servers ?? [];
  const startDisabledReason = isImpersonating
    ? 'Starting a GPU server is not available while impersonating (staff: use the admin area).'
    : activeServerCount(allServers) >= GPU_SERVER_MAX_ACTIVE
      ? `You already run the maximum number of GPU servers (${GPU_SERVER_MAX_ACTIVE}).`
      : null;

  const renderBalanceStrip = () => {
    if (walletError !== null) {
      return (
        <Alert
          variant="warning"
          isInline
          isPlain
          title="Could not load your prepaid balance"
          className="pf-v6-u-mb-md"
          actionLinks={<AlertActionLink onClick={() => void refetchWallet()}>Retry</AlertActionLink>}
        >
          {walletError}
        </Alert>
      );
    }
    if (wallet === null) {
      return walletLoading ? (
        <Flex
          className="pf-v6-u-mb-md"
          spaceItems={{ default: 'spaceItemsSm' }}
          alignItems={{ default: 'alignItemsCenter' }}
        >
          <FlexItem>
            <Spinner size="md" aria-label="Loading your prepaid balance" />
          </FlexItem>
          <FlexItem>Loading your prepaid balance…</FlexItem>
        </Flex>
      ) : null;
    }

    const needed = billedServers(allServers).length > 0 ? nextHourTotal(allServers) : null;
    const short = needed !== null ? isShortOf(wallet.balance, needed) : null;
    const nextDue = earliestPaidThrough(allServers);

    return (
      <div className="pf-v6-u-mb-md">
        <Flex spaceItems={{ default: 'spaceItemsMd' }} alignItems={{ default: 'alignItemsCenter' }}>
          <FlexItem>
            Prepaid balance: <BalanceAmount value={wallet.balance} />
          </FlexItem>
          <FlexItem>
            <Button variant="secondary" onClick={() => setIsTopUpOpen(true)}>
              Top up
            </Button>
          </FlexItem>
        </Flex>
        {short === true && needed !== null && (
          <Alert
            variant="warning"
            isInline
            title="Your balance doesn't cover the next hour of your servers"
            className="pf-v6-u-mt-sm"
          >
            Your balance doesn&apos;t cover the next hour of your servers (≈ {formatPrice(needed)} needed). Servers
            whose next hour cannot be paid are deleted — with their data — when their paid hour ends
            {nextDue !== null ? ` (next: ${formatDateTime(nextDue)})` : ''}, unless you top up.
          </Alert>
        )}
      </div>
    );
  };

  const renderTable = (rows: GpuServer[]) => (
    <Table aria-label="GPU servers" variant="compact">
      <Thead>
        <Tr>
          <Th>Hostname</Th>
          <Th>Server</Th>
          <Th>Location</Th>
          <Th>Status</Th>
          <Th>IP</Th>
          <Th>Price / hour incl. VAT</Th>
          <Th>Billing</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((server) => (
          <Tr key={server.id}>
            <Td dataLabel="Hostname">
              <Link to={`/datacenter/gpu-servers/${server.id}`}>
                <span className="pf-v6-u-font-family-monospace">{server.hostname}</span>
              </Link>
            </Td>
            <Td dataLabel="Server">{server.product?.label ?? '—'}</Td>
            <Td dataLabel="Location">{server.location_code}</Td>
            <Td dataLabel="Status">
              <GpuServerStatusLabel status={server.status} />
            </Td>
            <Td dataLabel="IP">
              {server.ip !== null ? (
                <ClipboardCopy
                  variant="inline-compact"
                  hoverTip="Copy"
                  clickTip="Copied"
                  copyAriaLabel={`Copy the IP address of ${server.hostname}`}
                >
                  {server.ip}
                </ClipboardCopy>
              ) : (
                '—'
              )}
            </Td>
            <Td dataLabel="Price / hour incl. VAT" modifier="nowrap">
              {formatPrice(server.first_hour_ttc)}
            </Td>
            <Td dataLabel="Billing">{billingCell(server)}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const renderContent = () => {
    if (firstLoad) {
      return <CenteredSpinner />;
    }

    if (error !== null) {
      return (
        <EmptyState
          titleText="Unable to load your GPU servers"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()} isLoading={loading} isDisabled={loading}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (allServers.length === 0) {
      return (
        <>
          {renderBalanceStrip()}
          <EmptyState titleText="No GPU servers yet" icon={MicrochipIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>
              Start a GPU or CPU server by the hour. Each hour is paid in advance from your prepaid balance.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <StartServerButton disabledReason={startDisabledReason} />
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </>
      );
    }

    const visible = showDeleted ? allServers : allServers.filter((server) => !isTerminal(server.status));
    const hiddenCount = allServers.length - visible.length;

    return (
      <>
        {renderBalanceStrip()}
        {pollError !== null && (
          <Alert
            variant="warning"
            isInline
            isPlain
            title="The statuses could not be refreshed — showing the last known state"
            className="pf-v6-u-mb-sm"
          >
            {pollError}
          </Alert>
        )}
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem alignSelf="center">
              <Switch
                id="gpu-servers-show-deleted"
                label="Show deleted servers"
                isChecked={showDeleted}
                onChange={(_event, checked) => setShowDeleted(checked)}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        {visible.length > 0 ? (
          renderTable(visible)
        ) : (
          <Content component="p" className="pf-v6-u-text-color-subtle">
            You have no active GPU servers. {hiddenCount} deleted or ended server{hiddenCount === 1 ? ' is' : 's are'}{' '}
            hidden — turn on &ldquo;Show deleted servers&rdquo; to see {hiddenCount === 1 ? 'it' : 'them'}.
          </Content>
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="GpuServersBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>GPU Servers</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <MicrochipIcon />
              </Icon>
              GPU servers
            </Content>
            <Content component="p">Powerful servers by the hour, paid from your prepaid balance.</Content>
          </FlexItem>
          {activeTab === 'servers' && (
            <FlexItem>
              <Flex spaceItems={{ default: 'spaceItemsSm' }}>
                {!firstLoad && (
                  <FlexItem>
                    <StartServerButton disabledReason={startDisabledReason} />
                  </FlexItem>
                )}
                <FlexItem>
                  <Button
                    variant="secondary"
                    icon={<SyncAltIcon />}
                    isLoading={loading}
                    spinnerAriaValueText="Refreshing your GPU servers"
                    onClick={() => {
                      if (!loading) {
                        void refetch();
                        // The balance strip reads the wallet (database-only, cheap): refresh it too.
                        void refetchWallet();
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </FlexItem>
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTab}
          onSelect={(_event, tabKey) => {
            if (isGpuServersTabKey(tabKey)) {
              selectTab(tabKey);
            }
          }}
          aria-label="GPU server sections"
          mountOnEnter
        >
          <Tab eventKey="servers" title={<TabTitleText>Servers</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <GpuServerImpersonationAlert className="pf-v6-u-mb-md" />
              {renderContent()}
            </div>
          </Tab>
          <Tab eventKey="budget" title={<TabTitleText>Budget</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <GpuBudgetTab isActive={activeTab === 'budget'} />
            </div>
          </Tab>
        </Tabs>
      </PageSection>

      <TopUpModal
        isOpen={isTopUpOpen}
        onClose={() => setIsTopUpOpen(false)}
        onCredited={() => void refetchWallet()}
        wallet={wallet}
        defaultPaymentMethod={defaultPaymentMethod}
        walletLoading={walletLoading}
        walletError={walletError}
      />
    </>
  );
}

export default GpuServers;
